import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  Button,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  TabContent,
  TabPane,
} from 'reactstrap'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import useAxios from 'axios-hooks'
import { shade } from 'polished'
import { format } from 'date-fns'
import { enUS, ptBR } from 'date-fns/locale'
import _ from 'lodash'
import * as Icon from 'react-feather'
import { NumberParam, StringParam, withDefault } from 'use-query-params'

import useWindowDimensions from '~/hooks/useWindowDimensions'
import { CallbackDeleteAlert } from '~/components/CallbackDeleteAlert'
import Pagination from '~/components/ecto/Pagination'
import { Hide, Show } from '~/components/commons'
import Breadcrumb from '~/components/Breadcrumb'
import { CustomTable, TableRow, Content } from '~/components/ecto/CustomTable'
import SchedulesFilterPanel from './SchedulesFilterPanel'
import { Body, Header, Container, SubHeader, ContentWrapper } from './styles'
import { CommaArrayParam, useQueryParamsReplace } from '~/hooks/useQueryParams'

function DeleteSchedulesButton({
  setScheduleToRemove,
  schedule,
  removingSchedule,
  openModal,
  isPublished,
}) {
  const intl = useIntl()
  function handleScheduleRemove() {
    openModal(true)
    setScheduleToRemove(schedule)
  }

  return (
    <ActionMenu>
      {!isPublished && (
        <Link to={`schedule/${schedule.id}`}>
          <Icon.Edit3 height={18} />
        </Link>
      )}
      <ButtonErase
        onClick={() => handleScheduleRemove()}
        disabled={removingSchedule}
        title={
          isPublished
            ? intl.formatMessage({ id: 'Delete post publication' })
            : intl.formatMessage({ id: 'Cancel schedule' })
        }
      >
        {isPublished ? <Icon.Trash2 height={18} /> : <Icon.Slash height={18} />}
      </ButtonErase>
    </ActionMenu>
  )
}

function Publications() {
  const [isVisibleSideMenu, setIsVisibleSideMenu] = useState(false)
  const [search, setSearch] = useState('')
  const [confirmModal, setConfirmModal] = useState(false)
  const [fetchingSchedules, setFetchingSchedules] = useState(true)
  const [fetchingPosts, setFetchingPosts] = useState(true)
  const [scheduleToRemove, setScheduleToRemove] = useState({})
  const [removingSchedule, setRemovingSchedule] = useState(false)
  const [tabId, setTabId] = useState(0)

  const [queryParams, setQueryParams] = useQueryParamsReplace({
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 25),
    search: StringParam,
    scheduleType: withDefault(CommaArrayParam, ['all']),
  })

  const history = useHistory()
  const projectId = useSelector(state => state.user?.selectedProject?.id)

  const { width } = useWindowDimensions()
  const MOBILE_MODE_BREAKPOINT = 1280
  const isMobileMode = useMemo(() => width < MOBILE_MODE_BREAKPOINT, [width])

  const intl = useIntl()

  const [, removeScheduleAxios] = useAxios(
    {
      method: 'DELETE',
    },
    { manual: true }
  )

  const [
    {
      data: { result: schedules = [], total: totalSchedules } = {},
      loading: fetchingSchedulesQuery,
    },
    fetchSchedules,
  ] = useAxios(
    {
      method: 'GET',
      url: `/projects/${projectId}/schedules`,
      params: {
        type: 'scheduled',
      },
    },
    { useCache: false, manual: true }
  )

  const [
    {
      data: { result: posts = [], total: totalPosts } = {},
      loading: fetchingPostsQuery,
    },
    fetchPosts,
  ] = useAxios(
    {
      method: 'GET',
      url: `/projects/${projectId}/schedules`,
      params: {
        type: 'posted',
      },
    },
    { useCache: false, manual: true }
  )

  const handleFetchSchedules = useCallback(
    _.debounce(() => {
      if (!fetchingSchedules) setFetchingSchedules(true)

      const url = `/projects/${projectId}/schedules${history.location.search}`

      fetchSchedules({ url }).finally(_ => setFetchingSchedules(false))
    }, 250),
    [fetchSchedules, projectId]
  )

  const handleFetchPosts = useCallback(
    _.debounce(() => {
      if (!fetchingPosts) setFetchingPosts(true)

      const url = `/projects/${projectId}/schedules${history.location.search}`
      fetchPosts({ url }).finally(_ => setFetchingPosts(false))
    }, 250),
    [fetchPosts, projectId]
  )

  useEffect(() => {
    handleFetchSchedules()
    handleFetchPosts()
  }, [queryParams, handleFetchSchedules, handleFetchPosts])

  const setSearchParam = useCallback(
    _.debounce(function (search) {
      setQueryParams({ ...queryParams, search })
    }, 500),
    []
  )

  const tableHeader = [
    { name: 'Resume', width: '30%', style: { paddingLeft: '30px' } },
    { name: 'Type', center: true, width: '10%' },
    { name: 'Date', center: true, width: '15%' },
    { name: 'Locations', center: true, width: '10%' },
    { name: 'Project', center: true, width: '30%' },
    { name: '_blank', center: true, width: '5%' },
  ]

  const scrollToTopTable = () => {
    document.getElementById('schedules-table').scrollIntoView()
  }

  const scheduleTypesIcons = {
    News: <Icon.Edit height={60} />,
    Event: <Icon.Edit height={60} />,
    Offer: <Icon.Edit height={60} />,
    'Covid-19': <Icon.Edit height={60} />,
    'Edit Images': <Icon.Map height={60} />,
    'General Info': <Icon.Map height={60} />,
    'Edit FAQ': <Icon.Map height={60} />,
    Question: <Icon.HelpCircle height={60} />,
    Review: <Icon.Star height={60} />,
    Unspecified: <Icon.Folder heigth={60} />,
  }

  Object.freeze(scheduleTypesIcons)

  const removeSchedule = async () => {
    setRemovingSchedule(true)

    removeScheduleAxios({
      url: `/projects/${projectId}/schedules/${scheduleToRemove.id}`,
    })
      .then(() => {
        toast.success(
          intl.formatMessage({ id: 'Schedule deleted successfully!' })
        )

        if (!scheduleToRemove.publishedAt) {
          const scheduleIndex = schedules.findIndex(
            schedule => schedule.id === scheduleToRemove.scheduleId
          )
          if (scheduleIndex !== -1) schedules.splice(scheduleIndex, 1)
        }
      })
      .catch(e => {
        toast.error(intl.formatMessage({ id: 'Error on delete schedule' }))
        console.error(e)
      })
      .finally(() => {
        setRemovingSchedule(false)
      })
  }

  function handleNavClick(id) {
    if (id === tabId) return
    const tabsNav = document.querySelector('.nav.nav-tabs')
    const tabs = tabsNav.childNodes
    tabs.forEach((tab, index) => {
      if (index === id) return tab.classList.add('active')
      tab.classList.remove('active')
    })

    setTabId(id)
  }

  return (
    <>
      <Header>
        <Breadcrumb
          paths={[
            {
              render: () => (
                <Link to="/">
                  <Icon.Home size={20} />
                </Link>
              ),
            },
            { text: 'Publications' },
          ]}
        />
      </Header>

      <Container>
        <ContentWrapper>
          <SchedulesFilterPanel
            isVisibleSideMenu={isVisibleSideMenu}
            setIsVisibleSideMenu={setIsVisibleSideMenu}
            isMobileMode={isMobileMode}
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
          <Body>
            <StyledNav tabs>
              <NavItem className="active" onClick={() => handleNavClick(0)}>
                <NavLink>
                  {intl.formatMessage({ id: 'Scheduled publications' })}
                  {fetchingSchedules ? (
                    <StyledSpan className="ml-1 rounded-circle badge badge-primary">
                      ...
                    </StyledSpan>
                  ) : (
                    <StyledSpan className="ml-1 rounded-circle badge badge-primary">
                      {totalSchedules > 1000 ? '99+' : totalSchedules}
                    </StyledSpan>
                  )}
                </NavLink>
              </NavItem>
              <NavItem onClick={() => handleNavClick(1)}>
                <NavLink>{intl.formatMessage({ id: 'Published' })}</NavLink>
              </NavItem>
            </StyledNav>
            <TabContent activeTab={tabId}>
              <TabPane tabId={0}>
                <SubHeader>
                  <strong>
                    <Show
                      condition={
                        !_.isEmpty(schedules) &&
                        !fetchingSchedulesQuery &&
                        !fetchingSchedules
                      }
                    >
                      <FormattedMessage
                        id="Found Schedules"
                        values={{
                          totalCount: intl.formatNumber(totalSchedules || 0),
                        }}
                      />
                    </Show>
                    <Show
                      condition={fetchingSchedulesQuery && fetchingSchedules}
                    >
                      <FormattedMessage id="Fetching Schedules..." />
                    </Show>
                  </strong>
                </SubHeader>

                <div className="d-flex flex-column mt-2">
                  <Show condition={fetchingSchedulesQuery && fetchingSchedules}>
                    <Spinner style={{ margin: '0 auto' }} color="primary" />
                  </Show>
                  <Show
                    condition={
                      _.isEmpty(schedules) &&
                      !fetchingSchedulesQuery &&
                      !fetchingSchedules
                    }
                  >
                    <span className="text-center">
                      <FormattedMessage id="No results found" />
                    </span>
                  </Show>

                  <Show
                    condition={
                      !_.isEmpty(schedules) &&
                      !fetchingSchedulesQuery &&
                      !fetchingSchedules
                    }
                  >
                    <CustomTable
                      search
                      searchPlaceholder="Search schedule..."
                      setSearch={search => {
                        setSearchParam(search)
                        setSearch(search)
                      }}
                      disabled={fetchingSchedulesQuery}
                      searchValue={search}
                      headers={tableHeader}
                      loading={fetchingSchedulesQuery}
                      id="schedules-table"
                    >
                      {schedules.map(schedule => (
                        <TableRow key={schedule?.id}>
                          <Content text="big">
                            <div className="d-flex align-items-center">
                              <AvatarContainer>
                                {scheduleTypesIcons[schedule?.type]}
                              </AvatarContainer>
                              {schedule?.title}
                            </div>
                          </Content>
                          <Content>
                            <FormattedMessage id={schedule?.type} />
                          </Content>
                          <Content>
                            {schedule?.scheduledAt
                              ? format(new Date(schedule?.scheduledAt), 'P p', {
                                  locale: intl.locale === 'en' ? enUS : ptBR,
                                })
                              : 'No date'}
                          </Content>
                          <Content>
                            <Icon.MapPin className="mr-1" />
                            {schedule?.locations?.length ?? (
                              <FormattedMessage id="No data" />
                            )}
                          </Content>
                          <Content>
                            <Icon.Grid className="mr-1" />
                            {schedule?.project?.name}
                          </Content>
                          <Content>
                            <Hide condition={!schedule.publishedPosts}>
                              <DeleteSchedulesButton
                                setScheduleToRemove={setScheduleToRemove}
                                schedule={schedule}
                                removingSchedule={removingSchedule}
                                openModal={setConfirmModal}
                                isPublished={!_.isEmpty(schedule.publishedAt)}
                              />
                            </Hide>
                          </Content>
                        </TableRow>
                      ))}
                    </CustomTable>

                    <Pagination
                      showLimitPerPage
                      onPageChange={() => scrollToTopTable()}
                      onLimitChange={() => scrollToTopTable()}
                      total={totalSchedules}
                    />
                  </Show>
                </div>
              </TabPane>
              <TabPane tabId={1}>
                <SubHeader>
                  <strong>
                    <Show
                      condition={
                        !_.isEmpty(posts) &&
                        !fetchingPosts &&
                        !fetchingPostsQuery
                      }
                    >
                      <FormattedMessage
                        id="Found Posts"
                        values={{
                          totalCount: intl.formatNumber(totalPosts || 0),
                        }}
                      />
                    </Show>
                    <Show condition={fetchingPosts && fetchingPostsQuery}>
                      <FormattedMessage id="Fetching Posts..." />
                    </Show>
                  </strong>
                </SubHeader>

                <div className="d-flex flex-column mt-2">
                  <Show condition={fetchingPosts && fetchingSchedules}>
                    <Spinner style={{ margin: '0 auto' }} color="primary" />
                  </Show>
                  <Show
                    condition={
                      _.isEmpty(posts) && !fetchingPostsQuery && !fetchingPosts
                    }
                  >
                    <span className="text-center">
                      <FormattedMessage id="No results found" />
                    </span>
                  </Show>

                  <Show
                    condition={
                      !_.isEmpty(posts) && !fetchingPosts && !fetchingPostsQuery
                    }
                  >
                    <CustomTable
                      search
                      searchPlaceholder="Search post..."
                      setSearch={search => {
                        setSearchParam(search)
                        setSearch(search)
                      }}
                      disabled={fetchingPosts}
                      searchValue={search}
                      headers={tableHeader}
                      loading={fetchingPosts}
                      id="posts-table"
                    >
                      {posts.map(post => (
                        <TableRow key={post?.id}>
                          <Content text="big">
                            <div className="d-flex align-items-center">
                              <AvatarContainer>
                                {scheduleTypesIcons[post?.type]}
                              </AvatarContainer>
                              {post?.title}
                            </div>
                          </Content>
                          <Content>
                            <FormattedMessage id={post?.type} />
                          </Content>
                          <Content>
                            {post?.scheduledAt
                              ? format(new Date(post?.scheduledAt), 'P p', {
                                  locale: intl.locale === 'en' ? enUS : ptBR,
                                })
                              : 'No date'}
                          </Content>
                          <Content>
                            <Icon.MapPin className="mr-1" />
                            {post?.locations?.length ?? (
                              <FormattedMessage id="No data" />
                            )}
                          </Content>
                          <Content>
                            <Icon.Grid className="mr-1" />
                            {post?.project?.name}
                          </Content>
                          <Content>
                            <Hide
                              condition={
                                post?.publishedAt && !post.publishedPosts
                              }
                            >
                              <DeleteSchedulesButton
                                setScheduleToRemove={setScheduleToRemove}
                                schedule={post}
                                removingSchedule={removingSchedule}
                                openModal={setConfirmModal}
                                isPublished={!_.isEmpty(post.publishedAt)}
                              />
                            </Hide>
                          </Content>
                        </TableRow>
                      ))}
                    </CustomTable>

                    <Pagination
                      showLimitPerPage
                      onPageChange={() => scrollToTopTable()}
                      onLimitChange={() => scrollToTopTable()}
                      total={totalPosts}
                    />
                  </Show>
                </div>
              </TabPane>
            </TabContent>
            <CallbackDeleteAlert
              open={confirmModal}
              setOpen={setConfirmModal}
              warnTitle={
                scheduleToRemove.publishedAt
                  ? 'Do you want to remove the post?'
                  : 'Do you want to remove the schedule?'
              }
              warnMessage={
                scheduleToRemove.publishedAt
                  ? 'Confirming this action will delete the publication in all published locations.'
                  : 'Confirming this action will delete the post schedule.'
              }
              successTitle="Deleted"
              successMessage={
                scheduleToRemove.publishedAt
                  ? 'Posts deleted successfully'
                  : 'Schedule successfully deleted'
              }
              errorTitle="Error"
              errorMessage={
                scheduleToRemove.publishedAt
                  ? 'Failed to delete post'
                  : 'Failed to delete schedule'
              }
              callback={removeSchedule}
            />
          </Body>
        </ContentWrapper>
      </Container>
    </>
  )
}

const ActionMenu = styled.div`
  display: flex;
  gap: 1rem;

  a:nth-child(1) {
    color: white;
    width: 34px;
    height: 34px;

    background-color: #c4c4c4 !important;
    border-color: #ed5f5f !important;
    border-radius: 50%;
    padding: 0px 5px;
    margin-right: 10px;
    max-height: 34px;
    transition-duration: 0.2s;

    &:hover {
      background-color: ${shade(0.2, '#c4c4c4')} !important;
      color: white;
    }

    &:disabled {
      opacity: 1;
    }

    svg {
      margin-right: 12px;
      margin-top: 5px;
    }
  }
`

const ButtonErase = styled(Button)`
  color: white;
  width: 34px;
  height: 34px;

  background-color: #ed5f5f !important;
  border-color: #ed5f5f !important;
  border-radius: 50%;
  padding: 0px 5px;
  margin-right: 10px;
  max-height: 34px;
  transition-duration: 0.2s;

  &:hover {
    background-color: ${shade(0.2, '#ED5F5F')} !important;
    color: white;
  }

  &:disabled {
    opacity: 1;
  }

  svg {
    margin-right: 12px;
  }
`

const AvatarContainer = styled.div`
  background-color: #fff;
  min-width: 56px;
  max-width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;

  img {
    max-width: 100%;
  }
`

const StyledNav = styled(Nav)`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 2em;
  align-items: center;
  border-bottom: 1px solid rgba(196, 196, 196, 0.2) !important;
  padding: 0;
  box-shadow: none !important;
  overflow-x: auto;
  overflow-y: hidden;

  > li {
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: auto;
    height: 100%;
    font-size: 1.1rem;
    padding: 1em 1.5em 0.5em 1.5em;
    cursor: pointer;
    .nav-link {
      color: #d2d2ff !important;
    }
  }

  > li.active {
    .nav-link {
      color: #0091ff !important;
    }
    border-bottom: 3px solid #0091ff;
  }
`

const StyledSpan = styled.span`
  width: 24px;
  height: 24px;
  text-align: center;
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
  background-color: #c4c4c4;
`

export default Publications
